// VELLA 2.0 — OnboardingWizard
// 4-step setup flow shown once per user (gated on user.onboardingComplete).
// Step 1: business basics + marketing goal
// Step 2: connect Facebook/Instagram (Meta OAuth), Wix, Google Ads (Google OAuth)
// Step 3: brand setup — logo upload, brand colors, tagline
// Step 4: ready summary + jump to Vella Relay
//
// On completion, calls API.updateMe({ onboardingComplete: true }) and hands
// off to the parent via onComplete(). Step 1 + Step 3 also save to the
// Business profile via API.saveBusiness so context survives the wizard.

import React, { useState, useRef, useEffect } from "react";
import * as API from "../api";
import {
  FONT, BG, TEXT, TEXT2, TEXT3,
  BLUE, BLUE_LIGHT, BLUE_DEEP,
  BORDER, CARD, SUCCESS,
  SHADOW_SM,
  useIsMobile,
} from "../shared";

// ─── Theme primitives (kept local so this file stands alone) ─────────────
const GLASS_CARD = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.62) 100%)",
  backdropFilter: "blur(34px) saturate(200%)",
  WebkitBackdropFilter: "blur(34px) saturate(200%)",
  border: "1px solid rgba(255,255,255,0.65)",
  boxShadow:
    "0 1px 0 rgba(255,255,255,0.92) inset, 0 30px 80px -24px rgba(11,107,211,0.26), 0 12px 32px -8px rgba(14,165,233,0.16)",
  borderRadius: 26,
};

const INPUT = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 12,
  border: `1px solid ${BORDER}`,
  background: "rgba(255,255,255,0.78)",
  fontSize: 14,
  color: TEXT,
  fontFamily: FONT,
  outline: "none",
};

const LABEL = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: TEXT3,
  textTransform: "uppercase",
  letterSpacing: 1,
  marginBottom: 6,
};

const PRIMARY_BTN = {
  padding: "12px 22px",
  borderRadius: 999,
  border: "none",
  // Dark navy — premium / authoritative, not pastel.
  background: "linear-gradient(135deg, #1a3a6e 0%, #1a2d5a 60%, #0f1f3f 100%)",
  color: "#fff",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: FONT,
  boxShadow: "0 8px 24px -6px rgba(15, 31, 63, 0.55), 0 1px 0 rgba(255,255,255,0.06) inset",
};

const GHOST_BTN = {
  padding: "11px 20px",
  borderRadius: 999,
  border: `1px solid ${BORDER}`,
  background: "rgba(255,255,255,0.6)",
  color: TEXT2,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: FONT,
};

// ─── Inline SVG icons (no emojis per spec) ───────────────────────────────
const Icon = {
  check: (size = 14) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  arrowRight: (size = 14) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  meta: (size = 22) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#1877F2">
      <path d="M22 12.07C22 6.48 17.52 2 11.93 2 6.34 2 1.86 6.48 1.86 12.07 1.86 17.1 5.55 21.27 10.4 22v-7.02H7.9v-2.91h2.5V9.9c0-2.47 1.47-3.84 3.72-3.84 1.08 0 2.21.19 2.21.19v2.43h-1.25c-1.23 0-1.61.76-1.61 1.55v1.86h2.74l-.44 2.91h-2.3V22C18.31 21.27 22 17.1 22 12.07z" />
    </svg>
  ),
  instagram: (size = 22) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#E1306C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="#E1306C" stroke="none" />
    </svg>
  ),
  wix: (size = 22) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#000">
      <path d="M5.6 6.5l1.5 8.5 1.7-6.7c.2-.7.5-1 .9-1.2.4-.2.9-.2 1.4 0 .4.2.7.5.9 1.2l1.7 6.7 1.5-8.5h2L14.6 17h-2L11 10.4 9.4 17h-2L5.4 6.5h2.2zm14 0v10.6h-2V6.5h2z" />
    </svg>
  ),
  google: (size = 22) => (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.5 12.27c0-.79-.07-1.55-.2-2.27H12v4.3h5.92c-.26 1.38-1.04 2.55-2.21 3.34v2.77h3.57c2.09-1.93 3.22-4.77 3.22-8.14z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 015.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 001 12c0 1.78.43 3.46 1.18 4.94l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  ),
  upload: (size = 18) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  sparkle: (size = 18) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2zm6 12l.8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8L18 14zm-12 1l.6 1.7L8.3 17l-1.7.6L6 19.3l-.6-1.7L3.7 17l1.7-.6L6 15z" />
    </svg>
  ),
};

// ─── Step header ─────────────────────────────────────────────────────────
function StepDots({ step, total = 4 }) {
  return (
    <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 28 }}>
      {Array.from({ length: total }).map((_, i) => {
        const idx = i + 1;
        const done = idx < step;
        const active = idx === step;
        return (
          <div
            key={i}
            style={{
              width: active ? 28 : 10,
              height: 10,
              borderRadius: 999,
              background: done || active ? BLUE : "rgba(191,219,247,0.6)",
              transition: "all 0.25s ease",
            }}
          />
        );
      })}
    </div>
  );
}

// ─── Industry + goal options ─────────────────────────────────────────────
const INDUSTRIES = [
  { value: "restaurant", label: "Restaurant" },
  { value: "fitness", label: "Fitness" },
  { value: "beauty", label: "Beauty" },
  { value: "retail", label: "Retail" },
  { value: "services", label: "Services" },
  { value: "agency", label: "Agency" },
  { value: "other", label: "Other" },
];

const GOALS = [
  { value: "more_customers", label: "More customers" },
  { value: "more_sales", label: "More sales" },
  { value: "brand_awareness", label: "Brand awareness" },
  { value: "save_time", label: "Save time" },
];

// ─── Connect Card (Step 2) ───────────────────────────────────────────────
function ConnectCard({ icon, name, description, status, onConnect, disabled }) {
  const isConnected = status === "connected";
  return (
    <div
      style={{
        ...GLASS_CARD,
        borderRadius: 16,
        padding: "16px 18px",
        display: "flex",
        alignItems: "center",
        gap: 14,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: "rgba(255,255,255,0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 2 }}>
          {name}
        </div>
        <div style={{ fontSize: 12, color: TEXT2 }}>{description}</div>
      </div>
      {isConnected ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, color: SUCCESS, fontWeight: 700, fontSize: 12 }}>
          {Icon.check(14)}
          <span>Connected</span>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={onConnect}
          style={{
            ...GHOST_BTN,
            color: BLUE,
            border: `1px solid ${BLUE}`,
            background: BLUE_LIGHT,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          Connect
        </button>
      )}
    </div>
  );
}

// ─── Color swatch input (Step 3) ─────────────────────────────────────────
function ColorSwatch({ value, onChange, onRemove }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 12, background: "rgba(255,255,255,0.7)", border: `1px solid ${BORDER}` }}>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Pick brand color"
        style={{ width: 28, height: 28, border: "none", padding: 0, background: "transparent", cursor: "pointer" }}
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Brand color hex"
        spellCheck={false}
        style={{ ...INPUT, padding: "6px 8px", fontSize: 12, fontFamily: '"SF Mono", ui-monospace, monospace', width: 96, border: "none", background: "transparent" }}
      />
      <button
        type="button"
        onClick={onRemove}
        style={{ background: "transparent", border: "none", color: TEXT3, cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px" }}
        aria-label="Remove color"
      >
        ×
      </button>
    </div>
  );
}

// ─── Step persistence (so OAuth round-trip doesn't reset the wizard) ────
const STEP_KEY = "vella_onboarding_step";
function loadStep() {
  try {
    const raw = localStorage.getItem(STEP_KEY);
    const n = raw ? parseInt(raw, 10) : 1;
    return Number.isFinite(n) && n >= 1 && n <= 4 ? n : 1;
  } catch { return 1; }
}

// Light E.164 formatter for the business phone. 10 digits → assume US (+1);
// 11 digits starting with 1 → +<digits>; already-+ → keep. The backend
// normalizes again, so this is just a friendly best-effort.
function toE164(raw) {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  if (trimmed.startsWith("+")) return "+" + digits;
  if (digits.length === 10) return "+1" + digits;
  if (digits.length === 11 && digits.startsWith("1")) return "+" + digits;
  return "+" + digits;
}

// ─── The wizard itself ───────────────────────────────────────────────────
export default function OnboardingWizard({ user, onComplete, onJumpToRelay }) {
  const isMobile = useIsMobile();
  const [step, setStep] = useState(loadStep);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Persist step across OAuth popup round-trips.
  useEffect(() => {
    try { localStorage.setItem(STEP_KEY, String(step)); } catch {}
  }, [step]);

  // Step 1
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [phone, setPhone] = useState("");
  const [goal, setGoal] = useState("");

  // Step 2
  const [connecting, setConnecting] = useState(null); // "meta" | "wix" | null
  const [connections, setConnections] = useState({ meta: "idle", wix: "idle", google: "idle" });

  // Listen for Meta OAuth completion via postMessage from the backend callback.
  // Backend posts { type: 'vella:meta-oauth', success: bool } to window.opener.
  useEffect(() => {
    function onMessage(ev) {
      const data = ev?.data;
      if (!data || data.type !== "vella:meta-oauth") return;
      if (data.success !== false) {
        setConnections((c) => ({ ...c, meta: "connected" }));
      }
      setConnecting((cur) => (cur === "meta" ? null : cur));
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Step 3
  const fileInputRef = useRef(null);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [logoFileName, setLogoFileName] = useState(null);
  const [colors, setColors] = useState([BLUE]);
  const [tagline, setTagline] = useState("");

  // ── Step 1 → save business basics + advance ─────────────────────────
  const canAdvanceStep1 = businessName.trim().length >= 2 && !!industry && !!goal;

  async function advanceFromStep1() {
    if (!canAdvanceStep1 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Best-effort save — wizard still advances even if backend rejects
      // (e.g. older backend without new fields). User can re-enter via
      // Settings; we don't want to wedge them at step 1.
      try {
        await API.saveBusiness({
          name: businessName.trim(),
          industry,
          phone: toE164(phone),
          goals: GOALS.find((g) => g.value === goal)?.label || goal,
        });
      } catch (saveErr) {
        console.warn("[Onboarding] step 1 saveBusiness failed:", saveErr.message);
      }
      setStep(2);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Step 2 → kick off OAuth in a popup ─────────────────────────────
  async function connect(platform) {
    setConnecting(platform);
    setError(null);
    try {
      const data = platform === "wix"
        ? await API.getWixConnectUrl()
        : await API.getOAuthConnectUrl(platform); // "meta" → /api/oauth/meta/connect
      const url = data?.authUrl;
      if (!url) throw new Error("No auth URL returned");

      // Open OAuth in a popup so the wizard stays mounted. The callback
      // route on the backend redirects back to FRONTEND_URL — we listen
      // for the popup to close and re-check status.
      const popup = window.open(url, "vella-oauth", "width=620,height=720");
      const watcher = setInterval(async () => {
        if (!popup || popup.closed) {
          clearInterval(watcher);
          // Re-check connection status post-popup
          try {
            if (platform === "wix") {
              const s = await API.getWixStatus();
              setConnections((c) => ({ ...c, wix: s?.connected ? "connected" : "idle" }));
            } else if (platform === "google") {
              const s = await API.getOAuthStatus();
              const g = s?.platforms?.google_ads || s?.google_ads || s?.google;
              setConnections((c) => ({ ...c, google: g?.connected || g ? "connected" : "idle" }));
            } else {
              const s = await API.getOAuthStatus();
              setConnections((c) => ({
                ...c,
                meta: s?.facebook || s?.instagram || s?.meta ? "connected" : "idle",
              }));
            }
          } catch { /* status check is best-effort */ }
          setConnecting(null);
        }
      }, 600);
    } catch (err) {
      setError(err.message || "Could not start OAuth");
      setConnecting(null);
    }
  }

  // ── Step 3 → file picker ────────────────────────────────────────────
  function onLogoSelected(file) {
    if (!file) return;
    if (!/^image\//.test(file.type)) { setError("Logo must be an image"); return; }
    if (file.size > 5 * 1024 * 1024) { setError("Logo must be under 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setLogoDataUrl(reader.result);
      setLogoFileName(file.name);
      setError(null);
    };
    reader.readAsDataURL(file);
  }

  function addColor() {
    if (colors.length >= 3) return;
    setColors((c) => [...c, "#0A1B3A"]);
  }
  function setColor(i, v) { setColors((c) => c.map((x, idx) => (idx === i ? v : x))); }
  function removeColor(i) { setColors((c) => c.filter((_, idx) => idx !== i)); }

  async function advanceFromStep3() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Save brand setup if anything was entered. Logo as data-URL is fine
      // for now — backend stores up to 5 MB; can be swapped for upload
      // pipeline later without changing this call.
      const hasBrand = logoDataUrl || colors.length > 0 || tagline.trim();
      if (hasBrand) {
        try {
          await API.saveBusiness({
            name: businessName.trim() || "Vella Client",
            industry: industry || "other",
            logoUrl: logoDataUrl || undefined,
            brandColors: colors,
            tagline: tagline.trim() || undefined,
          });
        } catch (saveErr) {
          console.warn("[Onboarding] step 3 saveBusiness failed:", saveErr.message);
        }
      }
      setStep(4);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Step 4 → mark complete + hand off ───────────────────────────────
  // Persisting completion to the backend is mandatory — without it, the
  // gate in App.jsx will reopen this wizard on every reload. Verify the
  // PATCH response actually shows the flag flipped, and refresh getMe()
  // so the parent state matches what the server now persists.
  async function finish(jumpToRelay) {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const userId = user?.id;
    // Record completion client-side FIRST so the wizard can never reappear,
    // even if the backend can't persist the flag (e.g. prod DB missing the
    // onboardingComplete column pre-migration). The localStorage gate in
    // App.jsx reads `vella_onboarded_<userId>`.
    try { if (userId) localStorage.setItem(`vella_onboarded_${userId}`, "true"); } catch {}
    try {
      await API.updateMe({ onboardingComplete: true });
      try { localStorage.removeItem("vella_onboarding_pending"); } catch {}
    } catch (err) {
      // Don't wedge the user. Leave a pending flag so App.jsx re-attempts the
      // backend save on next load, and proceed regardless.
      console.warn("[Onboarding] finish backend save failed (proceeding anyway):", err?.message);
      try { localStorage.setItem("vella_onboarding_pending", "1"); } catch {}
    }
    try { localStorage.removeItem(STEP_KEY); } catch {}
    setSubmitting(false);
    if (typeof onComplete === "function") onComplete();
    if (jumpToRelay && typeof onJumpToRelay === "function") onJumpToRelay();
  }

  // ── Layout ──────────────────────────────────────────────────────────
  return (
    <div
      style={{
        display: "flex",
        alignItems: isMobile ? "flex-start" : "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: BG,
        padding: isMobile ? "max(16px, env(safe-area-inset-top)) 12px max(16px, env(safe-area-inset-bottom))" : 20,
        fontFamily: FONT,
      }}
    >
      <div style={{ ...GLASS_CARD, maxWidth: 540, width: "100%", padding: isMobile ? "26px 20px" : "40px 38px", maxHeight: isMobile ? "none" : "92vh", overflowY: isMobile ? "visible" : "auto", borderRadius: isMobile ? 22 : 26 }}>
        <StepDots step={step} />

        {/* ─── STEP 1 ─── */}
        {step === 1 && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: TEXT, margin: "0 0 6px", textAlign: "center", letterSpacing: "-0.02em" }}>
              Welcome to Vella
            </h1>
            <p style={{ fontSize: 14, color: TEXT2, textAlign: "center", margin: "0 0 28px" }}>
              {user?.name ? `Nice to meet you, ${user.name.split(" ")[0]}.` : "Let's get you set up."} A few quick questions.
            </p>

            <label style={LABEL}>Business name</label>
            <input
              style={{ ...INPUT, marginBottom: 18 }}
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="e.g. Joe's Coffee House"
              autoFocus
            />

            <label style={LABEL}>Industry</label>
            <select
              style={{ ...INPUT, marginBottom: 18, appearance: "none", backgroundImage: "linear-gradient(45deg, transparent 50%, " + TEXT3 + " 50%), linear-gradient(135deg, " + TEXT3 + " 50%, transparent 50%)", backgroundPosition: "calc(100% - 18px) 50%, calc(100% - 13px) 50%", backgroundSize: "5px 5px, 5px 5px", backgroundRepeat: "no-repeat" }}
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
            >
              <option value="">Select an industry</option>
              {INDUSTRIES.map((i) => (
                <option key={i.value} value={i.value}>{i.label}</option>
              ))}
            </select>

            <label style={LABEL}>Business phone number</label>
            <input
              style={{ ...INPUT, marginBottom: 6 }}
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. (336) 555-0142"
            />
            <p style={{ fontSize: 12, color: TEXT3, margin: "0 0 18px" }}>So Vella knows who's calling.</p>

            <label style={LABEL}>What's your main marketing goal?</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 6 }}>
              {GOALS.map((g) => {
                const selected = goal === g.value;
                return (
                  <button
                    key={g.value}
                    type="button"
                    onClick={() => setGoal(g.value)}
                    style={{
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: selected ? `2px solid ${BLUE}` : `1px solid ${BORDER}`,
                      background: selected ? BLUE_LIGHT : CARD,
                      color: selected ? BLUE_DEEP : TEXT2,
                      fontSize: 13,
                      fontWeight: selected ? 700 : 500,
                      cursor: "pointer",
                      fontFamily: FONT,
                      boxShadow: SHADOW_SM,
                      textAlign: "left",
                    }}
                  >
                    {g.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── STEP 2 ─── */}
        {step === 2 && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: TEXT, margin: "0 0 6px", textAlign: "center", letterSpacing: "-0.02em" }}>
              Connect your platforms
            </h1>
            <p style={{ fontSize: 13, color: TEXT2, textAlign: "center", margin: "0 0 24px" }}>
              Vella works best when she can read and post for you. You can skip and connect later.
            </p>

            <ConnectCard
              icon={<>{Icon.meta(20)}<span style={{ marginLeft: -4 }}>{Icon.instagram(16)}</span></>}
              name="Facebook & Instagram"
              description="Pages, ads, DMs, comments"
              status={connections.meta}
              onConnect={() => connect("meta")}
              disabled={connecting === "meta"}
            />
            <ConnectCard
              icon={Icon.wix(22)}
              name="Wix"
              description="Edit your website, run SEO"
              status={connections.wix}
              onConnect={() => connect("wix")}
              disabled={connecting === "wix"}
            />
            <ConnectCard
              icon={Icon.google(20)}
              name="Google Ads"
              description="Search, Display, Performance Max"
              status={connections.google}
              onConnect={() => connect("google")}
              disabled={connecting === "google"}
            />
          </div>
        )}

        {/* ─── STEP 3 ─── */}
        {step === 3 && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: TEXT, margin: "0 0 6px", textAlign: "center", letterSpacing: "-0.02em" }}>
              Brand setup
            </h1>
            <p style={{ fontSize: 13, color: TEXT2, textAlign: "center", margin: "0 0 24px" }}>
              Help Vella stay on-brand. Skip any of this if you're not ready.
            </p>

            <label style={LABEL}>Logo</label>
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                ...GLASS_CARD,
                borderRadius: 14,
                padding: "16px 18px",
                marginBottom: 18,
                display: "flex",
                alignItems: "center",
                gap: 14,
                cursor: "pointer",
              }}
            >
              <div style={{ width: 56, height: 56, borderRadius: 12, background: "rgba(255,255,255,0.85)", border: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                {logoDataUrl ? (
                  <img src={logoDataUrl} alt="logo preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                ) : (
                  <span style={{ color: TEXT3 }}>{Icon.upload(20)}</span>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 2 }}>
                  {logoFileName || "Upload your logo"}
                </div>
                <div style={{ fontSize: 12, color: TEXT3 }}>PNG or JPG, up to 5 MB</div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => onLogoSelected(e.target.files?.[0])}
              />
            </div>

            <label style={LABEL}>Brand colors (up to 3)</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
              {colors.map((c, i) => (
                <ColorSwatch key={i} value={c} onChange={(v) => setColor(i, v)} onRemove={() => removeColor(i)} />
              ))}
              {colors.length < 3 && (
                <button
                  type="button"
                  onClick={addColor}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 12,
                    border: `1px dashed ${BORDER}`,
                    background: "transparent",
                    color: TEXT3,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: FONT,
                  }}
                >
                  + Add color
                </button>
              )}
            </div>

            <label style={LABEL}>Tagline</label>
            <input
              style={INPUT}
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="e.g. Locally roasted, slowly brewed."
              maxLength={120}
            />
          </div>
        )}

        {/* ─── STEP 4 ─── */}
        {step === 4 && (
          <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: `linear-gradient(135deg, #38BDF8, ${BLUE})`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px", color: "#fff", boxShadow: "0 14px 32px -10px rgba(14,165,233,0.55)" }}>
              {Icon.check(28)}
            </div>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: TEXT, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
              You're ready
            </h1>
            <p style={{ fontSize: 14, color: TEXT2, margin: "0 0 22px" }}>
              Vella will call you with your first daily briefing tomorrow at 8am.
            </p>

            <div style={{ ...GLASS_CARD, borderRadius: 16, padding: "16px 18px", marginBottom: 22, textAlign: "left" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                Setup summary
              </div>
              <SummaryRow label="Business" value={businessName || "—"} />
              <SummaryRow label="Industry" value={INDUSTRIES.find((i) => i.value === industry)?.label || "—"} />
              <SummaryRow label="Main goal" value={GOALS.find((g) => g.value === goal)?.label || "—"} />
              <SummaryRow
                label="Connections"
                value={
                  [
                    connections.meta === "connected" && "Meta",
                    connections.wix === "connected" && "Wix",
                    connections.google === "connected" && "Google Ads",
                  ].filter(Boolean).join(", ") || "None yet"
                }
              />
              <SummaryRow
                label="Brand"
                value={
                  [logoDataUrl && "Logo", colors.length > 0 && `${colors.length} color${colors.length > 1 ? "s" : ""}`, tagline.trim() && "Tagline"]
                    .filter(Boolean).join(", ") || "Not set"
                }
                last
              />
            </div>

            <button
              type="button"
              onClick={() => finish(true)}
              disabled={submitting}
              style={{ ...PRIMARY_BTN, width: "100%", padding: "14px 22px", fontSize: 15, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >
              {Icon.sparkle(16)}
              <span>Talk to Vella</span>
              {Icon.arrowRight(14)}
            </button>
            <button
              type="button"
              onClick={() => finish(false)}
              disabled={submitting}
              style={{ ...GHOST_BTN, marginTop: 10, width: "100%" }}
            >
              I'll explore on my own
            </button>
          </div>
        )}

        {/* ─── Footer error ─── */}
        {error && (
          <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 12, background: "rgba(239,68,68,0.10)", color: "#B91C1C", fontSize: 12, fontWeight: 600 }}>
            {error}
          </div>
        )}

        {/* ─── Footer nav (steps 1-3) ─── */}
        {step < 4 && (
          <div style={{ display: "flex", flexDirection: isMobile ? "column-reverse" : "row", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: isMobile ? 10 : 0, marginTop: 28 }}>
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              style={{ ...GHOST_BTN, visibility: step === 1 ? "hidden" : "visible", display: step === 1 && isMobile ? "none" : (isMobile ? "block" : undefined), width: isMobile ? "100%" : undefined, minHeight: 44 }}
            >
              Back
            </button>

            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10, alignItems: isMobile ? "stretch" : "center", width: isMobile ? "100%" : "auto" }}>
              {(step === 2 || step === 3) && (
                <button
                  type="button"
                  onClick={() => (step === 2 ? setStep(3) : advanceFromStep3())}
                  style={{ ...GHOST_BTN, width: isMobile ? "100%" : undefined, minHeight: 44 }}
                  disabled={submitting}
                >
                  Skip for now
                </button>
              )}
              {step === 1 && (
                <button
                  type="button"
                  onClick={advanceFromStep1}
                  disabled={!canAdvanceStep1 || submitting}
                  style={{ ...PRIMARY_BTN, opacity: canAdvanceStep1 && !submitting ? 1 : 0.45, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, width: isMobile ? "100%" : undefined, minHeight: 48 }}
                >
                  Continue {Icon.arrowRight(13)}
                </button>
              )}
              {step === 2 && (
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  disabled={submitting}
                  style={{ ...PRIMARY_BTN, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, width: isMobile ? "100%" : undefined, minHeight: 48 }}
                >
                  Continue {Icon.arrowRight(13)}
                </button>
              )}
              {step === 3 && (
                <button
                  type="button"
                  onClick={advanceFromStep3}
                  disabled={submitting}
                  style={{ ...PRIMARY_BTN, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, width: isMobile ? "100%" : undefined, minHeight: 48 }}
                >
                  Continue {Icon.arrowRight(13)}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryRow({ label, value, last }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 12,
        padding: "8px 0",
        borderBottom: last ? "none" : "1px solid rgba(191,219,247,0.4)",
      }}
    >
      <span style={{ fontSize: 12, color: TEXT3, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13, color: TEXT, fontWeight: 600, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value}
      </span>
    </div>
  );
}
